import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KenteCanvas } from "./KenteCanvas";
import { PatternSelector } from "./PatternSelector";
import { SymbolSelector } from "./SymbolSelector";
import { BottomSheet } from "./BottomSheet";
import {
  GraduationCap,
  Users,
  Lock,
  ArrowLeft,
  Download,
  Save,
  X,
  Menu,
  X as CloseIcon,
  Type,
  Shapes,
} from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Canvas as FabricCanvas } from "fabric";

interface Symbol {
  id: string;
  name: string;
  meaning: string;
  image: string;
  category: "adinkra" | "other";
}

interface TextBox {
  id: string;
  text: string;
  color: string;
  left: number;
  top: number;
  fontSize: number;
}

interface ClassDesignerProps {
  className: string;
  templateId: string;
  templateName: string;
}

export const ClassDesigner = ({
  className,
  templateId,
  templateName,
}: ClassDesignerProps) => {
  const [selectedPattern, setSelectedPattern] = useState(templateId);
  const [selectedSymbols, setSelectedSymbols] = useState<Symbol[]>([]);
  const [textBoxes, setTextBoxes] = useState<TextBox[]>([]);
  const [sashText, setSashText] = useState("");
  const [textColor, setTextColor] = useState("#FFD700");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [bottomSheetType, setBottomSheetType] = useState<
    "symbols" | "text" | null
  >(null);

  // Canvas ref for saving designs
  const canvasRef = useRef<FabricCanvas | null>(null);

  // Save design states
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [designName, setDesignName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleAddSymbol = (symbol: Symbol) => {
    setSelectedSymbols((prev) => [...prev, symbol]);
  };

  const handleRemoveSymbol = (symbolId: string) => {
    setSelectedSymbols((prev) =>
      prev.filter((symbol) => symbol.id !== symbolId)
    );
  };

  const handleSaveDesign = async () => {
    if (!designName.trim()) {
      toast.error("Please enter a design name");
      return;
    }

    if (!canvasRef.current) {
      toast.error("Canvas not available for saving");
      return;
    }

    setIsSaving(true);
    try {
      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast.error("Please login to save designs");
        return;
      }

      // Get canvas data
      const canvasData = canvasRef.current.toJSON();

      // Prepare design data
      const designData = {
        canvas: canvasData,
        pattern: selectedPattern,
        symbols: selectedSymbols,
        textBoxes: textBoxes,
        sashText: sashText,
        textColor: textColor,
        templateName: templateName,
        className: className,
      };

      // Save to database
      const { error } = await supabase.from("customer_designs").insert({
        customer_id: user.id,
        design_name: designName.trim(),
        design_data: designData,
      });

      if (error) throw error;

      toast.success("Design saved successfully!");
      setShowSaveDialog(false);
      setDesignName("");
    } catch (error: any) {
      console.error("Error saving design:", error);
      toast.error("Failed to save design. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddText = () => {
    const newTextBox: TextBox = {
      id: `text-${Date.now()}`,
      text: sashText || "New Text",
      color: textColor,
      left: 200,
      top: 300,
      fontSize: 32,
    };
    setTextBoxes((prev) => [...prev, newTextBox]);
    setSashText("");

    if (bottomSheetType === "text") {
      closeBottomSheet();
    }
  };

  const handleRemoveText = (textId: string) => {
    setTextBoxes((prev) => prev.filter((textBox) => textBox.id !== textId));
  };

  const openBottomSheet = (type: "symbols" | "text") => {
    setBottomSheetType(type);
    setBottomSheetOpen(true);
  };

  const closeBottomSheet = () => {
    setBottomSheetOpen(false);
    setBottomSheetType(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs sm:text-sm"
                >
                  <ArrowLeft className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Back to Home</span>
                  <span className="sm:hidden">Back</span>
                </Button>
              </Link>
              <div>
                <h1 className="text-sm sm:text-xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                  <span className="hidden sm:inline">Class Designer</span>
                  <span className="sm:hidden">Designer</span>
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    <GraduationCap className="h-3 w-3 mr-1" />
                    {className}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Lock className="h-3 w-3 mr-1" />
                    {templateName}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSaveDialog(true)}
              >
                <Save className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Save Design</span>
                <span className="sm:hidden">Save</span>
              </Button>
              <Button size="sm">
                <Download className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Download</span>
                <span className="sm:hidden">Download</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-4 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 h-[calc(100vh-120px)] lg:h-[calc(100vh-120px)]">
          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar Controls - Hidden on mobile for class designer */}
          <aside className="hidden lg:block w-[350px] space-y-6 overflow-y-auto flex-shrink-0">
            {/* Class Info Card */}
            <Card className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <GraduationCap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{className}</h3>
                  <p className="text-sm text-muted-foreground">
                    Class Designer
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Template: {templateName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    Restricted to this template only
                  </span>
                </div>
              </div>
            </Card>

            {/* Text Customization */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Text Customization</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="sash-text">Add Text</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="sash-text"
                      placeholder="e.g., Class of 2025"
                      value={sashText}
                      onChange={(e) => setSashText(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      onClick={handleAddText}
                      disabled={!sashText.trim()}
                      size="sm"
                    >
                      Add
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="text-color">Text Color</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      id="text-color"
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-12 h-10 p-1 border rounded cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      placeholder="#FFD700"
                      className="flex-1 text-sm"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <SymbolSelector onAddSymbol={handleAddSymbol} />
            </Card>

            {textBoxes.length > 0 && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4">Text Elements</h2>
                <div className="space-y-2">
                  {textBoxes.map((textBox) => (
                    <div
                      key={textBox.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded border"
                          style={
                            {
                              backgroundColor: textBox.color,
                            } as React.CSSProperties
                          }
                        />
                        <span className="text-sm font-medium truncate">
                          {textBox.text}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveText(textBox.id)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </aside>

          {/* Canvas Area */}
          <main className="order-first lg:order-none flex-1 flex flex-col min-h-0">
            <Card className="p-4 sm:p-6 lg:p-8 bg-card flex-1 flex flex-col min-h-0">
              <div className="mb-4 lg:hidden">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="mr-2 h-4 w-4" />
                  Design Options
                </Button>
              </div>
              <div className="flex-1 min-h-0">
                <KenteCanvas
                  selectedPattern={selectedPattern}
                  textBoxes={textBoxes}
                  symbols={selectedSymbols}
                  onRemoveTextBox={handleRemoveText}
                  onRemoveSymbol={handleRemoveSymbol}
                  canvasRef={canvasRef}
                />
              </div>
            </Card>

            {/* Mobile Bottom Buttons */}
            <div className="lg:hidden fixed bottom-4 left-4 right-4 z-30">
              <div className="flex gap-3">
                <Button
                  onClick={() => openBottomSheet("symbols")}
                  className="flex-1 h-12 bg-primary text-primary-foreground"
                  size="lg"
                >
                  <Shapes className="mr-2 h-5 w-5" />
                  Symbols
                </Button>
                <Button
                  onClick={() => openBottomSheet("text")}
                  className="flex-1 h-12 bg-secondary text-secondary-foreground"
                  size="lg"
                >
                  <Type className="mr-2 h-5 w-5" />
                  Text
                </Button>
              </div>
            </div>

            {/* Mobile Bottom Sheets */}
            <BottomSheet
              isOpen={bottomSheetOpen && bottomSheetType === "symbols"}
              onClose={closeBottomSheet}
              title="Symbols"
            >
              <div className="p-6">
                <SymbolSelector
                  onAddSymbol={handleAddSymbol}
                  onClose={closeBottomSheet}
                />
              </div>
            </BottomSheet>

            <BottomSheet
              isOpen={bottomSheetOpen && bottomSheetType === "text"}
              onClose={closeBottomSheet}
              title="Text Customization"
            >
              <div className="p-6 space-y-6">
                <div>
                  <Label htmlFor="mobile-sash-text">Add Text</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="mobile-sash-text"
                      placeholder="e.g., Class of 2025"
                      value={sashText}
                      onChange={(e) => setSashText(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      onClick={handleAddText}
                      disabled={!sashText.trim()}
                      size="sm"
                    >
                      Add
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="mobile-text-color">Text Color</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      id="mobile-text-color"
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-12 h-10 p-1 border rounded cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      placeholder="#FFD700"
                      className="flex-1 text-sm"
                    />
                  </div>
                </div>

                {textBoxes.length > 0 && (
                  <div>
                    <Label>Text Elements</Label>
                    <div className="space-y-2 mt-2">
                      {textBoxes.map((textBox) => (
                        <div
                          key={textBox.id}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded border"
                              style={
                                {
                                  backgroundColor: textBox.color,
                                } as React.CSSProperties
                              }
                            />
                            <span className="text-sm font-medium truncate">
                              {textBox.text}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveText(textBox.id)}
                            className="h-8 w-8 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </BottomSheet>

            {/* Save Design Dialog */}
            {showSaveDialog && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                  <CardHeader>
                    <CardTitle>Save Design</CardTitle>
                    <CardDescription>
                      Give your design a name to save it
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="design-name">Design Name</Label>
                      <Input
                        id="design-name"
                        value={designName}
                        onChange={(e) => setDesignName(e.target.value)}
                        placeholder="My Kente Design"
                        className="mt-2"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowSaveDialog(false);
                          setDesignName("");
                        }}
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveDesign}
                        disabled={isSaving || !designName.trim()}
                      >
                        {isSaving ? "Saving..." : "Save Design"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
